import React, { useCallback, useMemo, useRef, useEffect } from "react";
import { SearchOption, TypeaheadInput, TypeaheadInputComponent, Flex } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { filterText } from "#components/styles_vanilla.css";
import { TextWithIcon } from "#components/TextWithIcon";
import { useGetDataVaultEntitiesLazyQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { SelectedTag } from "./SelectedTag";
import { EntitySearchProps } from "./types";

/**
 * Entity Search Component
 *
 * A reusable typeahead input for selecting investment entities. This component supports dependent
 * filtering where entity selection depends on a client (arena) being selected first.
 *
 * @param props - EntitySearchProps
 * @returns A typeahead input component for entity selection with dependent filtering
 *
 * @example
 * // Standalone usage with dependent client
 * <EntitySearch
 *   clientId="arena-123"
 *   value="entity-456"
 *   onSelect={(entityId) => setSelectedEntity(entityId)}
 *   placeholder="Select an entity"
 * />
 *
 * @example
 * // Without client selected (disabled state)
 * <EntitySearch
 *   clientId={undefined}
 *   onSelect={handleEntitySelect}
 *   infoText="Please select a client first"
 * />
 */
export const EntitySearch = (props: EntitySearchProps) => {
  const {
    clientIds,
    value: _value,
    values = [],
    multiselect = false,
    onSelect,
    onMultiSelect,
    onSearchClear,
    placeholder = "Search for an entity",
    infoText = "Please select the client first",
    className,
    disabled = false,
    isClearable = true,
    isFullWidth = true,
    testId = "entity-filter",
  } = props;

  const [fetchDataVaultEntities, { data: dataVaultData, loading: dataVaultLoading }] =
    useGetDataVaultEntitiesLazyQuery();
  const componentRef = useRef<TypeaheadInputComponent>(null);

  // Cache to store entity names by ID to avoid losing labels when search results change
  const entityNamesCache = useRef<Record<string, string>>({});

  // Always use DataVault query for both single and multiselect modes
  const effectiveClientIds = useMemo(() => clientIds || [], [clientIds]);
  const isEnabled = effectiveClientIds.length > 0;

  // Clear input when client(s) change
  useEffect(() => {
    componentRef?.current?.clearInputValue();
  }, [clientIds]);

  // Extracting the entity list from the response
  const entityLists: SearchOption[] = useMemo(() => {
    // Always use DataVault query data
    const entities = dataVaultData?.dataVaultEntities?.edges || [];

    const list = entities.map((edge: { node: { id: string; name: string } }) => ({
      id: edge.node.id,
      label: edge.node.name,
    }));

    // Update cache with new entity names
    list.forEach((entity: SearchOption) => {
      entityNamesCache.current[entity.id] = entity.label;
    });

    return list;
  }, [dataVaultData]);

  // Get selected entity names for multiselect tags
  const selectedEntities = useMemo(() => {
    if (!multiselect || !values.length) return [];

    return values.map((id) => {
      // First try to find in current search results, then fall back to cache
      const entity = entityLists.find((entity) => entity.id === id);
      const cachedName = entityNamesCache.current[id];

      return {
        id,
        label: entity?.label || cachedName || `Entity ${id}`,
      };
    });
  }, [multiselect, values, entityLists]);

  const handleSelection = useCallback(
    (selectedOption?: SearchOption) => {
      if (!selectedOption?.id) return;

      const selectedId = selectedOption.id.toString();

      // Cache the entity name immediately when selected
      if (selectedOption.label) {
        entityNamesCache.current[selectedId] = selectedOption.label;
      }

      if (multiselect) {
        if (!onMultiSelect) {
          return;
        }

        // Check if already selected
        if (values.includes(selectedId)) {
          return; // Don't add duplicates
        }

        const newValues = [...values, selectedId];
        onMultiSelect(newValues);
      } else {
        onSelect(selectedId);
      }
    },
    [onSelect, onMultiSelect, multiselect, values],
  );

  const handleRemoveTag = useCallback(
    (idToRemove: string) => {
      if (!onMultiSelect) return;

      const newValues = values.filter((id) => id !== idToRemove);
      onMultiSelect(newValues);
    },
    [values, onMultiSelect],
  );

  const handleDebouncedSearch = useCallback(
    async (searchValue?: string) => {
      if (searchValue?.length && isEnabled) {
        // Always use DataVaultEntities query
        await fetchDataVaultEntities({
          context: clientContext(),
          variables: {
            arenaIds: effectiveClientIds,
            searchTerm: searchValue,
          },
          fetchPolicy: "no-cache",
        });
      } else {
        if (multiselect) {
          // In multiselect mode, don't clear selections when search is cleared
          return;
        }
        onSearchClear?.();
      }
    },
    [effectiveClientIds, fetchDataVaultEntities, onSearchClear, multiselect, isEnabled],
  );

  // Conditional rendering based on multiselect mode
  if (multiselect) {
    return (
      <Flex flexDirection="column" gap={Sizes.SM}>
        {!isEnabled && <TextWithIcon label={infoText} iconType={"exclamation-circle"} />}
        {selectedEntities.length > 0 && (
          <Flex flexWrap="wrap" gap={Sizes.XS}>
            {selectedEntities.map((entity) => (
              <SelectedTag
                key={entity.id}
                label={entity.label}
                onRemove={() => handleRemoveTag(entity.id)}
                testId={`${testId}-tag-${entity.id}`}
              />
            ))}
          </Flex>
        )}
        <TypeaheadInput
          className={className || filterText}
          data-testid={testId}
          ref={componentRef}
          isClearable={false} // Disable clear button in multiselect mode
          isFullWidth={isFullWidth}
          disabled={disabled || !isEnabled}
          isLoading={dataVaultLoading}
          options={entityLists}
          onSelectOption={handleSelection}
          onDebouncedSearch={handleDebouncedSearch}
          placeholder={placeholder}
        />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap={Sizes.SM}>
      {!isEnabled && <TextWithIcon label={infoText} iconType={"exclamation-circle"} />}
      <TypeaheadInput
        className={className || filterText}
        data-testid={testId}
        ref={componentRef}
        isClearable={isClearable}
        isFullWidth={isFullWidth}
        disabled={disabled || !isEnabled}
        isLoading={dataVaultLoading}
        options={entityLists}
        onSelectOption={handleSelection}
        onDebouncedSearch={handleDebouncedSearch}
        placeholder={placeholder}
      />
    </Flex>
  );
};
